import React, { useState, useCallback } from 'react'
import { addMonths, differenceInCalendarMonths, format, lastDayOfMonth } from 'date-fns'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Button, Checkbox, Select, FormField, DateRangePicker, Dimmer, InfoBox } from '../../../../components'
import styles from './LeaseTermForm.module.scss'
import { userApiSelectors } from 'modules/api/user'
import { useDispatch, useSelector } from 'react-redux'
import { chatbotEvents, chatbotSelectors, getLeaseTermInitialValues } from 'modules/ui/ChatBot'
import { portfolioApiSelectors } from 'modules/api/portfolio'
import RouterPromptModal from 'components/organisms/Modals/RouterPromptModal/RouterPromptModal'
import SvgInfo from 'views/components/atoms/Icons/Controls/Info'

const validationSchema = yup.object().shape({
  startDate: yup.string().required('required.'),
  endDate: yup.string().required('required.'),
  rolloverMonthToMonth: yup.boolean().required('required.'),
})

// Display-only option shown when the term doesn't match a preset duration
// (e.g. the end date was picked manually). It is not part of the selectable
// dropdown options — it only reflects the current state.
const CUSTOM_DURATION_OPTION = { value: null, label: 'Custom' }

type LeaseTermFormProps = {
  portfolioId: string
  redirect: (url: string) => void
}

const LeaseTermForm = ({ portfolioId, redirect }: LeaseTermFormProps) => {
  const dispatch = useDispatch()
  const currentPortfolioId = useSelector(chatbotSelectors.getCurrentPortfolioId)
  const initialValues = useSelector(getLeaseTermInitialValues)
  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const isDraftPortfolio = useSelector(state => portfolioApiSelectors.isDraftPortfolio(state)(currentPortfolioId))

  const changeDurationHelper = useCallback((from, value) => lastDayOfMonth(addMonths(new Date(from), value - 1)), [])

  const getDurationOptions = useCallback(
    () =>
      [24, 12, 6, 3, 1].map((i: any) => ({
        value: i,
        label: `${i} Month${i > 1 ? 's' : ''}`,
      })),
    [],
  )

  // Derive the duration from the saved dates so the Select reflects the actual term
  // rather than a hardcoded default. Falls back to null for a custom (non-option) term.
  const getInitialDuration = useCallback(
    (startDate, endDate) => {
      const months = differenceInCalendarMonths(new Date(endDate), new Date(startDate)) + 1
      return getDurationOptions().some((o: any) => o.value === months) ? months : null
    },
    [getDurationOptions],
  )

  const handleSubmit = (values: any): void => {
    dispatch(chatbotEvents.termValuesSubmitted(values))
  }

  return (
    <div className={styles.root}>
      <Formik
        className={styles.form}
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, handleBlur, handleSubmit, submitForm, values, dirty, isValid, isSubmitting }) => {
          const [duration, setDuration] = useState<number | null>(() =>
            getInitialDuration(values.startDate, values.endDate),
          )
          const [leaseStartDate, setLeaseStartDate] = useState(new Date(values.startDate))
          const [leaseEndDate, setLeaseEndDate] = useState(new Date(values.endDate))

          return (
            <form className={styles.form} onSubmit={handleSubmit}>
              <RouterPromptModal
                when={dirty}
                isValid={isValid}
                isSubmitting={isSubmitting}
                onSave={submitForm}
                onDiscard={() => true}
              />
              <fieldset disabled={isReadOnly}>
                <div className={styles.body}>
                  <div className={`${styles['lease-term-row']} rbn--row`}>
                    {!isDraftPortfolio && (
                      <div className="rbn--col-md-8">
                        <InfoBox type="info" className={styles['info-box']}>
                          <header>
                            <SvgInfo />
                            <p>If you intend to renew this lease, use the Renewal step instead.</p>
                          </header>
                        </InfoBox>
                      </div>
                    )}
                    {isDraftPortfolio && (
                      <div className="rbn--col-md-4">
                        <FormField>
                          <Select
                            name="duration"
                            label="Duration"
                            labelHint="Selecting a duration automatically calculates your end date based on your start date. The duration itself is not saved."
                            options={getDurationOptions()}
                            value={
                              getDurationOptions().find((o: any) => o.value === duration) || CUSTOM_DURATION_OPTION
                            }
                            onChange={({ value }: any) => {
                              const endDate = changeDurationHelper(values.startDate, value)
                              setDuration(value)
                              setFieldValue('endDate', endDate)
                              setLeaseEndDate(endDate)
                            }}
                          />
                        </FormField>
                      </div>
                    )}
                    <div className={isDraftPortfolio ? 'rbn--col-md-2' : 'rbn--col-md-4'}>
                      <div className={styles['start-date']}>
                        <span>Start date</span>
                        <p>{leaseStartDate ? format(leaseStartDate, 'd MMMM yyyy') : ''}</p>
                      </div>
                    </div>
                    <div className={isDraftPortfolio ? 'rbn--col-md-2' : 'rbn--col-md-4'}>
                      <div className={styles['end-date']}>
                        <span>End date</span>
                        <p>{leaseEndDate ? format(leaseEndDate, 'd MMMM yyyy') : ''}</p>
                      </div>
                    </div>
                  </div>
                  <div className="rbn--row">
                    <div className="rbn--col-md-8">
                      <Dimmer active={isReadOnly} bgColor="white">
                        <FormField>
                          <DateRangePicker
                            startDate={leaseStartDate}
                            endDate={leaseEndDate}
                            startLabel="Start date"
                            endLabel="End date"
                            onChange={(date: any, isStartDate: any) => {
                              if (isStartDate) {
                                setLeaseStartDate(date)
                                setFieldValue('startDate', format(date, 'yyyy-MM-dd'))
                                // Recalculate the end date from the selected duration (if any).
                                if (duration) {
                                  const endDate = changeDurationHelper(date, duration)
                                  setFieldValue('endDate', endDate)
                                  setLeaseEndDate(endDate)
                                }
                              } else {
                                setLeaseEndDate(date)
                                setFieldValue('endDate', format(date, 'yyyy-MM-dd'))
                                // Manually picking an end date breaks the duration link.
                                setDuration(null)
                              }
                            }}
                            inline
                          />
                        </FormField>
                      </Dimmer>
                    </div>
                  </div>
                  <div className="rbn--row">
                    <div className="rbn--col-md-8">
                      <InfoBox type="locked" className={styles['info-box']}>
                        <FormField>
                          <Checkbox
                            name="rolloverMonthToMonth"
                            label="Switch to month-to-month on expiry"
                            labelHint="If enabled, invoices will continue to generate monthly after the end date until it is disabled."
                            checked={values.rolloverMonthToMonth}
                            onChange={(e: any) => setFieldValue('rolloverMonthToMonth', e.checked)}
                            onBlur={handleBlur}
                          />
                        </FormField>
                      </InfoBox>
                    </div>
                  </div>
                </div>
                <div className={isDraftPortfolio ? styles.footer : styles['footer--active']}>
                  {isDraftPortfolio ? (
                    <>
                      <Button onClick={() => redirect(`/leases/${portfolioId}/edit/tenants`)}>Back</Button>
                      {/* The dates are pre-filled with a valid default term and the rollover toggle is
                          optional, so advancing must not require any changes. */}
                      <Button type="submit">Next</Button>
                    </>
                  ) : (
                    <Button disabled={!dirty} type="submit">
                      Save
                    </Button>
                  )}
                </div>
              </fieldset>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}

export default LeaseTermForm
